import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../store/auth-context';
import { login as loginService, loginepub, getOrganisation } from '../services/notification.services';
import { parseJwtToken } from '../utils/jwt.utils';
import { validateEmail } from '../utils/validation.utils';

interface LoginCredentials {
  email: string;
  password: string;
}

interface EpubLoginCredentials {
  epubtoken: string;
}

interface UserData {
  uuid: string;
  user_id: string;
  name: string;
  uid: string;
  usertype: string;
  orgName: string;
  orgLogoUrl: string;
  userPermission?: string;
  token: string;
  refreshToken: string;
  orgId: string;
  orgWisitiaID: string;
  epubOrgId: string;
  thumbImage: string;
  CHANNEL_PUSH_NOTIFICATION: boolean;
  EDITOR_ATTACHMENT: boolean;
  EDITOR_EXTERNAL_LINK: boolean;
  EDITOR_LINK: boolean;
  EDITOR_TEXT: boolean;
  EDIT_EMOJI: boolean;
  EDIT_EPUB: boolean;
  superAdminId?: string;
  superAdminToken?: string;
  superAdminName?: string;
}

/**
 * Custom hook for handling user login
 * Centralizes login logic and error handling
 */
export const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const processLoginResponse = useCallback(async (udata: any) => {
    if (!udata.token) {
      throw new Error('No token received from server');
    }

    const tokenUserData = parseJwtToken(udata.token);
    const orgId = tokenUserData.user.org;
    const uid = tokenUserData.user.id;

    let userData: UserData;

    // OWNER and INTERNALADMIN users don't belong to any specific organization
    if (udata.usertype === 'OWNER' || udata.usertype === 'INTERNALADMIN') {
      userData = {
        uuid: udata.userId,
        user_id: udata.user_id,
        name: `${udata.firstName} ${udata.lastName}`,
        uid: uid,
        usertype: udata.usertype,
        orgName: '',
        orgLogoUrl: '',
        userPermission: udata.userPermission,
        token: udata.token,
        refreshToken: udata.refreshToken,
        orgId: orgId || '',
        orgWisitiaID: '',
        epubOrgId: '',
        thumbImage: udata.thumbImage,
        CHANNEL_PUSH_NOTIFICATION: true,
        EDITOR_ATTACHMENT: true,
        EDITOR_EXTERNAL_LINK: true,
        EDITOR_LINK: true,
        EDITOR_TEXT: true,
        EDIT_EMOJI: true,
        EDIT_EPUB: true
      };
    } else {
      // Fetch organization details for regular users
      const orgResponse = await getOrganisation(orgId, udata.token);
      
      // getOrganisation API returns the org object directly, not wrapped in status/error/response
      // It only returns { status: 400/404, error: 'message' } on error
      if (!orgResponse || (orgResponse.status && (orgResponse.status === 400 || orgResponse.status === 404))) {
        throw new Error(orgResponse?.error || 'Could not retrieve organization details');
      }

      userData = {
        uuid: udata.userId,
        user_id: udata.user_id,
        name: `${udata.firstName} ${udata.lastName}`,
        uid: uid,
        usertype: udata.usertype,
        orgName: orgResponse.name,
        orgLogoUrl: orgResponse.logoUrl,
        userPermission: udata.userPermission,
        token: udata.token,
        refreshToken: udata.refreshToken,
        orgId: orgId,
        orgWisitiaID: orgResponse.org_wistia_project_id,
        epubOrgId: orgResponse.epubOrgId,
        thumbImage: udata.thumbImage,
        CHANNEL_PUSH_NOTIFICATION: orgResponse.CHANNEL_PUSH_NOTIFICATION,
        EDITOR_ATTACHMENT: orgResponse.EDITOR_ATTACHMENT,
        EDITOR_EXTERNAL_LINK: orgResponse.EDITOR_EXTERNAL_LINK,
        EDITOR_LINK: orgResponse.EDITOR_LINK,
        EDITOR_TEXT: orgResponse.EDITOR_TEXT,
        EDIT_EMOJI: orgResponse.EDIT_EMOJI,
        EDIT_EPUB: orgResponse.EDIT_EPUB
      };
    }

    authCtx.login(userData);
    authCtx.setUProfileImage(userData.thumbImage);

    // Handle super admin login
    if (udata.usertype === 'OWNER' || udata.usertype === 'INTERNALADMIN') {
      localStorage.setItem('super-admin', udata.user_id);
      localStorage.setItem('super-admin-token', udata.token);
      localStorage.setItem('super-admin-name', `${udata.firstName} ${udata.lastName}`);
      localStorage.setItem('super-admin-usertype', udata.usertype);
      navigate('/Organisation/#users');
    } else {
      navigate('/notifications');
    }
  }, [authCtx, navigate]);

  const handleLogin = useCallback(async (credentials: LoginCredentials) => {
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      // Validation
      if (!validateEmail(credentials.email)) {
        throw new Error('Invalid email format');
      }

      if (!credentials.password) {
        throw new Error('Password is required');
      }

      const response = await loginService({
        email: credentials.email,
        password: credentials.password
      });

      // loginService returns response.data directly (not the full axios response)
      // Backend returns: { status: 200/400, error: string|null, response: object|null }
      // Success: { status: 200, error: null, response: { status: 'OK', token, userId, ... } }
      // Error: { status: 400, error: 'error message', response: null }
      
      if (!response) {
        throw new Error('Invalid response from server');
      }

      if (response.status === 400 || response.error) {
        throw new Error(response.error || 'Login failed');
      }

      // Success case - response.response contains the user data with status: 'OK'
      if (response.status === 200 && response.response) {
        await processLoginResponse(response.response);
      } else {
        throw new Error('Login failed - no response data received');
      }
    } catch (err) {
      // Handle both Error instances and ApiError objects
      const message = err instanceof Error 
        ? err.message 
        : (err as any)?.message || 'Login failed. Please try again.';
      if (isMountedRef.current) {
        setError(message);
      }
      toast.error(message);
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [processLoginResponse]);

  const handleEpubLogin = useCallback(async (credentials: EpubLoginCredentials) => {
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      if (!credentials.epubtoken) {
        throw new Error('Invalid ePublisher token');
      }

      const response = await loginepub({ epubtoken: credentials.epubtoken });

      // Check if login was successful
      // Handle both numeric status codes and string 'OK' status
      if (response.status === 400 || response.error) {
        throw new Error(response.error || 'ePublisher login failed');
      }

      if ((response.status === 200 || response.status === 'OK') && response.response) {
        await processLoginResponse(response.response);
      } else {
        throw new Error('ePublisher login failed - no response data received');
      }
    } catch (err) {
      // Handle both Error instances and ApiError objects
      const message = err instanceof Error 
        ? err.message 
        : (err as any)?.message || 'ePublisher login failed. Please try again.';
      if (isMountedRef.current) {
        setError(message);
      }
      toast.error(message);
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [processLoginResponse]);

  return {
    handleLogin,
    handleEpubLogin,
    isLoading,
    error,
    setError
  };
};
